import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IPayout } from '@curbnturf/entities';
import { from, mergeMap, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { BASE_API_URL } from '../network/api-config';

const API_URL = BASE_API_URL + 'user/financial';

/**
 * Local, direct-inject replacement for `@curbnturf/user-dashboard`'s
 * FinancialService — feature-cnt account components inject this class
 * directly (no DI-token override needed) rather than the real
 * `@curbnturf/user-dashboard` class. Reimplements only the two methods
 * cnt-landing actually calls (`findPendingPayouts`, `processPayoutRequest`)
 * — both genuinely await a resolved current user (`userFacade.currentUserOnce$`
 * in the real service), so unlike other forks in this migration this one
 * needs real logic, not just a pass-through. `getAdminPayouts`/
 * `completeRequest`/`remove` are not reimplemented — cnt-landing never calls
 * them.
 *
 * The server's `POST user/financial/payout` only reads `data.user.id` (see
 * server/src/user/user.controller.ts's requestPayout), so the reduced
 * IPublicUser id from AuthService is sufficient — no need to fetch the full
 * User record the real UserFacade-backed version supplied.
 */
@Injectable({ providedIn: 'root' })
export class LocalFinancialService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  processPayoutRequest(cntCash = false): Observable<IPayout> {
    return from(this.auth.currentUserOnceLoaded()).pipe(
      mergeMap((user) => this.http.post<IPayout>(`${API_URL}/payout`, { user: { id: user?.id }, cntCash })),
    );
  }

  findPendingPayouts(): Observable<IPayout[]> {
    return from(this.auth.currentUserOnceLoaded()).pipe(
      mergeMap((user) => this.http.get<IPayout[]>(`${API_URL}/payouts/${user?.id}?status=pending`, {})),
    );
  }
}
